if (!customElements.get('delivery-step')) {
	class DeliveryStep extends HTMLElement {
		connectedCallback() {
			this._updateContent();
			if (this.getAttribute('mobile-month-style')) {
				this._onBreakpointChange = this._updateContent.bind(this);
				this.breakpointQuery.addEventListener('change', this._onBreakpointChange);
			}
		}
		disconnectedCallback() {
			if (this._onBreakpointChange) {
				this.breakpointQuery.removeEventListener('change', this._onBreakpointChange);
				this._onBreakpointChange = null;
			}
		}
		get breakpointQuery() {
			if (!this._breakpointQuery) {
				const breakpoint = parseInt(this.getAttribute('mobile-breakpoint'), 10) || 768;
				this._breakpointQuery = window.matchMedia(`(min-width: ${breakpoint}px)`);
			}
			return this._breakpointQuery;
		}
		get monthStyle() {
			const mobileMonthStyle = this.getAttribute('mobile-month-style');
			if (mobileMonthStyle && !this.breakpointQuery.matches) {
				return mobileMonthStyle;
			}
			return 'long';
		}
		get minOffset() {
			if (this.lastStep) {
				return Math.max(this.offsets[0] - this.lastStep.maxOffset, 0);
			}
			return this.offsets[0];
		}
		get maxOffset() {
			if (this.lastStep) {
				return Math.max(this.offsets[1] - this.lastStep.maxOffset);
			}
			return this.offsets[1];
		}
		get offsets() {
			return JSON.parse(this.getAttribute('offsets'));
		}
		get vacationDays() {
			return JSON.parse(this.getAttribute('vacation-days'));
		}
		get cutOffTime() {
			return this.getAttribute('cut-off-time');
		}
		get weekendDays() {
			const weekendDays = JSON.parse(this.getAttribute('weekend-days'))
				? JSON.parse(this.getAttribute('weekend-days'))
				: [];
			return weekendDays.map(this._convertWeekdayNamesToNumber);
		}
		get startingDate() {
			if (this.lastStep) {
				return this.lastStep.maxDate;
			}
			return this.getAttribute('starting-date');
		}
		get dateFormat() {
			return this.getAttribute('date-format');
		}
		get ignoreCutOffTime() {
			return this.getAttribute('ignore-cut-off-time') === 'true';
		}
		get lastStep() {
			if (this.getAttribute('last-step-id')) {
				return document.getElementById(this.getAttribute('last-step-id'));
			}
			return null;
		}
		set minDate(date) {
			this.setAttribute('min-date', date);
		}
		set maxDate(date) {
			this.setAttribute('max-date', date);
		}
		get minDate() {
			return this.getAttribute('min-date');
		}
		get maxDate() {
			return this.getAttribute('max-date');
		}
		_convertWeekdayNamesToNumber(weekday) {
			const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
			return weekdays.indexOf(weekday.toLowerCase());
		}
		get locale() {
			return document.documentElement.lang || undefined;
		}
		_weekday(date) {
			return date.toLocaleDateString(this.locale, { weekday: 'short' }).replace(/\.$/, '');
		}
		_day(date) {
			return String(date.getDate()).padStart(2, '0');
		}
		_month(date, style) {
			const shortMonths = theme.strings.deliveryDateShortMonths;
			if (style === 'short' && Array.isArray(shortMonths) && shortMonths.length === 12) {
				return shortMonths[date.getMonth()].trim();
			}
			return date.toLocaleDateString(this.locale, { month: style });
		}
		_formatLongDate(date) {
			return theme.strings.deliveryDateLongDate
				.replace('{{weekday}}', this._weekday(date))
				.replace('{{day}}', this._day(date))
				.replace('{{month}}', this._month(date, this.monthStyle));
		}
		_formatDateRangeLong(minDate, maxDate) {
			if (minDate.getTime() === maxDate.getTime()) {
				return this._formatLongDate(minDate);
			}
			const month = this._month(minDate, this.monthStyle);
			const maxMonth = this._month(maxDate, this.monthStyle);
			if (month === maxMonth) {
				return theme.strings.deliveryDateLongTwoDatesSameMonth
					.replace('{{weekday}}', this._weekday(minDate))
					.replace('{{day}}', this._day(minDate))
					.replace('{{max_weekday}}', this._weekday(maxDate))
					.replace('{{max_day}}', this._day(maxDate))
					.replace('{{month}}', month);
			}
			return theme.strings.deliveryDateLongTwoDates
				.replace('{{date}}', this._formatLongDate(minDate))
				.replace('{{max_date}}', this._formatLongDate(maxDate));
		}
		_formatDate(minDate, maxDate) {
			if (this.dateFormat === 'long') {
				return this._formatDateRangeLong(minDate, maxDate);
			}
			const minDay = minDate.toLocaleString('default', { day: '2-digit' });
			const minMonth = minDate.toLocaleString('default', { month: 'short' });
			const maxDay = maxDate.toLocaleString('default', { day: '2-digit' });
			const maxMonth = maxDate.toLocaleString('default', { month: 'short' });
			if (this.minOffset !== this.maxOffset && minMonth === maxMonth) {
				return theme.strings.deliveryDateSameMonth
					.replace('{{day}}', minDay)
					.replace('{{month}}', minMonth)
					.replace('{{max_day}}', maxDay);
			} else if (this.minOffset !== this.maxOffset) {
				return theme.strings.deliveryDatesDiffMonth
					.replace('{{max_day}}', maxDay)
					.replace('{{max_month}}', maxMonth)
					.replace('{{day}}', minDay)
					.replace('{{month}}', minMonth);
			} else {
				return theme.strings.deliveryDateOneDate.replace('{{day}}', minDay).replace('{{month}}', minMonth);
			}
		}
		_addDaysSkippingWeekendsAndVacations(daysToAdd) {
			let currentDate = this.startingDate ? new Date(this.startingDate) : new Date();
			if (this._isStartingDateWeekendOrVacation()) {
				currentDate = this._getFirstNonWeekendOrVacationDay();
			}
			let addedDays = 0;
			while (addedDays < daysToAdd) {
				currentDate.setDate(currentDate.getDate() + 1);
				if (this.weekendDays.includes(currentDate.getDay())) continue;

				const dateString = currentDate.toISOString().split('T')[0];
				if (this.vacationDays.includes(dateString)) continue;
				addedDays++;
			}
			return currentDate;
		}
		_isStartingDateWeekendOrVacation() {
			const startingDate = this.startingDate ? new Date(this.startingDate) : new Date();
			const dateString = startingDate.toISOString().split('T')[0];
			return this.vacationDays.includes(dateString) || this.weekendDays.includes(startingDate.getDay());
		}
		_getFirstNonWeekendOrVacationDay() {
			let currentDate = this.startingDate ? new Date(this.startingDate) : new Date();
			while (true) {
				currentDate.setDate(currentDate.getDate() + 1);
				if (this.weekendDays.includes(currentDate.getDay())) continue;
				const dateString = currentDate.toISOString().split('T')[0];
				if (this.vacationDays.includes(dateString)) continue;
				break;
			}
			return currentDate;
		}
		_isAfterCutOffTime() {
			const now = this.startingDate ? new Date(this.startingDate) : new Date();

			const hours = now.getHours();
			const minutes = now.getMinutes();

			const currentTimeFormatted = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;

			return currentTimeFormatted > this.cutOffTime;
		}
		_updateContent() {
			const cutOffOffset = this.ignoreCutOffTime ? 0 : this._isAfterCutOffTime() ? 1 : 0;
			const minAddedDays = this.minOffset + cutOffOffset;
			const maxAddedDays = this.maxOffset + cutOffOffset;
			const minDate = this._addDaysSkippingWeekendsAndVacations(minAddedDays);
			const maxDate = this._addDaysSkippingWeekendsAndVacations(maxAddedDays);
			this.minDate = minDate.toISOString().split('T')[0];
			this.maxDate = maxDate.toISOString().split('T')[0];
			this.innerHTML = this._formatDate(minDate, maxDate);
		}
	}
	window.customElements.define('delivery-step', DeliveryStep);
}
